import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import styles from "../Scrollarea.module.css";

// Custom dot for color indicators
const StyledBadge = ({ color }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: color,
      display: "inline-block",
      mr: 1.2,
    }}
  />
);

const MediaEarning = () => {
  const { productTypeData } = useSelector(
    (state) => state.vendorDashboard?.vendorDashboardData
  );
  const { totalPaymentAmount } = useSelector(
    (state) => state.vendorDashboard?.vendorDashboardData
  );

  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const productColorMap = useMemo(() => {
    const map = {};
    Object.keys(productTypeData || {}).forEach((key) => {
      map[key] = getRandomColor();
    });
    return map;
  }, [productTypeData]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("Fabrication");

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (value) => {
    setSelectedPeriod(value || selectedPeriod);
    setAnchorEl(null);
  };

  const formatIndianCurrencyShort = (amount) => {
    if (typeof amount !== "number" || isNaN(amount)) return "0";

    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)} K`;
    } else {
      return amount.toString();
    }
  };

  const chartData = Object.entries(productTypeData || {}).map(
    ([key, value], index) => ({
      id: index,
      value: value.paymentAmount,
      label: key,
      color: productColorMap[key],
    })
  );

  return (
    <Card
      sx={{
        borderRadius: 5,
        boxShadow: 6,
        p: 2,
        width: "100%", // takes full width of parent
        minHeight: "100%",
        margin: "auto",
      }}
    >
      <Box
        mx={2}
        py={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderBottom={1}
        borderColor="divider"
      >
        <Typography variant="h5" fontWeight={700}>
          Media Earnings
        </Typography>

        <div>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            endIcon={<KeyboardArrowDown />}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderColor: "gray",
              color: "gray",
              borderRadius: 2,
            }}
          >
            {selectedPeriod}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleClose()}
          >
            <MenuItem onClick={() => handleClose("Fabrication")}>
              Fabrication
            </MenuItem>
            <MenuItem onClick={() => handleClose("Installation")}>
              Installation
            </MenuItem>
          </Menu>
        </div>
      </Box>

      <CardContent>
        {/* Donut Chart */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            mx: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#F6F8FA",
            borderRadius: 5,
            mb: 2,
          }}
        >
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius: 60,
                outerRadius: 110,
                paddingAngle: 3,
                cornerRadius: 9,
                startAngle: -90,
                endAngle: 270,
                cx: 130,
                cy: 130,
              },
            ]}
            width={260}
            height={260}
            slotProps={{ legend: { hidden: true } }}
          />

          {/* Centered Total */}
          {totalPaymentAmount !== 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-40%, -40%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography variant="h5" fontWeight={700} color="primary">
                {formatIndianCurrencyShort(totalPaymentAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Earnings
              </Typography>
            </Box>
          )}
        </Box>

        {/* Table Header */}
        <Grid
          container
          borderBottom={1}
          borderColor="divider"
          sx={{ mb: 1, backgroundColor: "#F8F9FA", p: 2 }}
        >
          <Grid item xs={6}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              fontWeight={600}
            >
              PRODUCTS
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              fontWeight={600}
            >
              QTY
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              fontWeight={600}
            >
              EARNINGS
            </Typography>
          </Grid>
        </Grid>

        {/* Table Rows */}
        <div
          className={`${styles.scrollContainerforDashboard} max-h-[200px] overflow-y-auto`}
        >
          {Object.entries(productTypeData || {}).map(([key, value], index) => (
            <Grid container key={index} alignItems="center" sx={{ py: 1.25 }}>
              <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                <StyledBadge color={productColorMap[key]} />
                <Typography variant="body2">{key}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  {value?.count}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" fontWeight={600}>
                  ₹ {formatIndianCurrencyShort(value?.paymentAmount)}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default MediaEarning;
